import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  SignModeType,
  StepperContext,
  useLoanOffer,
  useMicroFlow,
} from "../../contexts";
import {
  Button,
  Divider,
  PaymentScheduleRow,
  StepNextButton,
  Tooltip,
} from "../../components";
import {
  ButtonLoaderIcon,
  DocumentIcon,
  InfoIcon,
  ReadyClockIcon,
  SuccessIcon,
} from "../../assets/icons";
import LoanOfferRejectModel from "./LoanOfferRejectModal";
import {
  useApproveLoanOffer,
  useDirectDebitBankAccounts,
  useGetExistingShareholderSignature,
  useGetLoanOffer,
  useTheme,
  useDeclineLoanOffer,
} from "../../hooks";
import { useLocation } from "react-router-dom";
import { LOCALSTORAGE_KEYS } from "../../constants";
import { DateTime } from "luxon";
import {
  convertBnplMonthsToInstallments,
  formatAmount,
  getFutureDate,
  isEmptyObject,
} from "../../utils";
import {
  LOAN_PRODUCT_DISPLAY_NAME,
  LoanOfferStage,
  LoanProductType,
  LoanVendorType,
  LOSPlatform,
} from "../../hooks/api/types";
import { STEP_STAGE_NAME_MAP } from ".";
import { getCopyText } from "./constants";
import moment from "moment-timezone";
import { CredibleLogoLight } from "../../assets/images";
import { InterestFrequency } from "../../types";
import { format } from "date-fns";
import countries from "world-countries";
import LoanOfferError from "./LoanOfferError";

/**
 * Calculates the next stage in the loan offer process based on the provided data.
 * @returns {string} The next stage, represented by a constant from LoanOfferStage.
 */
export function calculateNextStage(
  isMainApplicant: boolean,
  hasBankAccounts: boolean,
  directDebitRequired = false
): LoanOfferStage {
  if (isMainApplicant || !directDebitRequired) {
    return hasBankAccounts
      ? LoanOfferStage.SELECT_BANK_ACCOUNT
      : LoanOfferStage.ADD_BANK_ACCOUNT;
  }

  return LoanOfferStage.SELECT_SIGN_MODE;
}
type LoanInfoItemProps = {
  label: string;
  text?: string | React.ReactNode;
  subText?: string | React.ReactNode;
  noDivider?: boolean;
  dividerClass?: string;
};

const LoanInfoItem = ({
  label,
  text,
  subText,
  noDivider,
  dividerClass,
}: LoanInfoItemProps) => (
  <div className="cx-w-full lg:cx-max-w-[420px]">
    <div className="cx-flex cx-flex-col cx-gap-y-1 cx-py-4">
      <div className="cx-text-text-secondary cx-text-sm cx-font-medium">
        {label}
      </div>
      <div className="cx-flex cx-flex-row cx-items-base cx-gap-x-1">
        {text && (
          <div className="cx-text-text-primary cx-font-medium">{text}</div>
        )}
        {subText && (
          <div className="cx-text-text-primary cx-font-bold cx-text-xl">
            {subText}
          </div>
        )}
      </div>
    </div>
    {!noDivider && <Divider className={dividerClass} />}
  </div>
);

const renderAED = (amount?: number | undefined) => (
  <span className="cx-inline-flex cx-items-center cx-gap-1">
    <div className="cx-font-aed-font"> D </div>
    {amount === null || amount === undefined ? "—" : formatAmount(amount)}
  </span>
);

const LoanOfferStart = () => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [cancelComment, setCancelComment] = useState(""); // comment for LOC cancel modal
  const {
    actions: { update: updateMicroFlowContext },
  } = useMicroFlow();

  const { setTheme, themeLoading } = useTheme();

  const { gotoStep, nextStep } = useContext(StepperContext);
  const {
    state: {
      isAlternateOffer,
      lastOfferExpiredAt,
      uiLocalizationData,
      id,
      loanConfig,
    },
    actions: { update },
  } = useLoanOffer();

  const {
    mutateAsync: getLoanOfferDetails,
    data,
    isLoading: loanOfferLoading,
    isError: loanOfferError,
  } = useGetLoanOffer();
  const { mutateAsync: getBankAccounts, data: bankAccounts } =
    useDirectDebitBankAccounts();
  const {
    mutateAsync: getExistingShareholderSignature,
    data: existingShareholderSignature,
  } = useGetExistingShareholderSignature();
  const { mutateAsync: approveLoanOffer } = useApproveLoanOffer();
  const { mutateAsync: declineLoanOffer, isLoading: declineLoading } =
    useDeclineLoanOffer();
  const [invoiceDueDate, setInvoiceDuedate] = useState("");
  const { search } = useLocation();
  const isLOC =
    data?.loanType === LoanProductType.LOC_INVOICE_DISCOUNTING ||
    data?.loanType === LoanProductType.LOC_PAYABLE_FINANCING;

  const vendors = data?.approvedVendors || [];

  useEffect(() => {
    const params = new URLSearchParams(search);
    const code = params.get("code");

    const handleCode = async () => {
      if (code) {
        localStorage.setItem(LOCALSTORAGE_KEYS.SESSION_TOKEN, code);
        await getLoanOfferDetails();

        if (data?.invoiceDueDate === null) {
          setInvoiceDuedate("Not Available");
        } else {
          setInvoiceDuedate(data?.invoiceDueDate);
        }
      }
    };

    handleCode();
  }, [search]);

  useEffect(() => {
    if (data?.dp?.themeConfig) {
      setTheme(data.dp.themeConfig);
      updateMicroFlowContext({
        logo: uiLocalizationData?.loanOfferMicroflow?.usePartnerLogo
          ? data?.dp?.themeConfig?.logo
          : CredibleLogoLight,
        footerLogo:
          (uiLocalizationData?.footer?.usePartnerLogo &&
            uiLocalizationData?.footer?.logoUrl) ||
          CredibleLogoLight,
      });
    }
  }, [
    loanOfferLoading,
    data?.dp?.themeConfig,
    uiLocalizationData?.loanOfferMicroflow?.usePartnerLogo,
  ]);

  useEffect(() => {
    if (
      (!data?.dp?.themeConfig || isEmptyObject(data?.dp?.themeConfig)) &&
      !loanOfferLoading &&
      !isLoading
    ) {
      setTheme();
    }
  }, [loanOfferLoading, isLoading, data?.dp?.themeConfig]);

  useEffect(() => {
    if (data) {
      if (data?.dp?.settings) {
        update({ dpSettings: data?.dp?.settings });
      }
      update({
        id: data.id,
        businessName: data.companyName,
        tradeLicenseNumber: data.licenseNumber,
        duration: data.loanInput?.duration,
        loanType: data.loanInput?.type,
        interestRate: data.interestRate,
        monthlyPayment: data.installmentAmount,
        totalAmount: data.approvedAmount,
        totalInterestPaid: data.totalInterestPaid,
        totalRepayment: data.totalAmount,
        loanApplicationId: data.loanApplicationId,
        stage: data.stage,
        isMainApplicant: data.isMainApplicant,
        totalShareholders: data.totalShareholders,
        shareholderId: data.shareholderId,
        isActive: data.isActive,
        lastOfferExpiredAt: data.lastOfferExpiredAt,
        isAlternateOffer: data.isAlternateOffer,
        preSelectedSignMode: data.preSelectedSignMode,
        uiLocalizationData: data.dp.uiLocalizationData,
        loanConfig: data.loanConfig,
        companyName: data.companyName,
        distributionPartnerId: data.dp.id,
        distributionPartnerName: data.dp.name,
      });

      if (data.preSelectedSignMode === SignModeType.PRINT_SCAN) {
        update({ signMode: SignModeType.PRINT_SCAN });
      } else {
        getExistingShareholderSignature({
          loanApplicationId: data.loanApplicationId,
          shareholderId: data.shareholderId,
        });
      }

      if (data?.loanConfig?.directDebitRequired) getBankAccounts(data.id);
      if (data?.stage !== LoanOfferStage.REVIEW) {
        gotoStep(STEP_STAGE_NAME_MAP[data?.stage as LoanOfferStage]);
      } else if (!data?.isMainApplicant) {
        if (data?.loanConfig?.directDebitRequired) {
          gotoStep(STEP_STAGE_NAME_MAP[LoanOfferStage.SELECT_SIGN_MODE]);
        } else {
          handleApprove();
        }
      } else {
        setIsLoading(false);
      }
    }
  }, [data]);

  useEffect(() => {
    if (bankAccounts?.length) {
      update({
        banklist: bankAccounts.map((account: any) => ({
          bankName: account.bankName,
          bankLogo: account.bankLogo,
          iban: account.iban,
          accountName: account.accountName,
          accountId: account.accountId,
          sourceType: account.sourceType,
        })),
      });
    }
  }, [bankAccounts]);

  useEffect(() => {
    if (existingShareholderSignature) {
      update({
        existingShareholderSignatureSignedUrl: existingShareholderSignature.url,
      });
    }
  }, [existingShareholderSignature]);

  const handleApprove = async () => {
    const isDirectDebitRequired = loanConfig?.directDebitRequired;
    if (isDirectDebitRequired) {
      gotoStep(STEP_STAGE_NAME_MAP[LoanOfferStage.SETUP_DIRECT_DEBIT]);
    } else {
      await approveLoanOffer({
        id: id || data.id,
      });
      gotoStep(STEP_STAGE_NAME_MAP[LoanOfferStage.COMPLETED]);
    }
    setIsLoading(false);
  };
  const handleDecline = () => {
    setShowRejectModal(true);
  };

  const handleDeclineConfirm = () => {
    if (isLOC) {
      const reason =
        (cancelComment || "").trim() || "Cancelled LOC application";
      update({ declineReason: reason }).finally(() => {
        declineLoanOffer({ id: id || data.id, reason })
          .then(() => {
            gotoStep([4, 2]);
            setShowRejectModal(false);
          })
          .catch(() => {
            setShowRejectModal(false);
          });
      });
      return;
    }
    gotoStep([4, 0]);
    setShowRejectModal(false);
  };

  // TODO: refactor this to a util function
  const formattedDate = useMemo(() => {
    if (lastOfferExpiredAt) {
      const date = moment.tz(
        lastOfferExpiredAt,
        DateTime.fromISO(lastOfferExpiredAt).toFormat("z")
      );
      const day = date.date();
      const suffix = () => {
        if (day === 1 || day === 21 || day === 31) return "st";
        else if (day === 2 || day === 22) return "nd";
        else if (day === 3 || day === 23) return "rd";
        else return "th";
      };

      return `${date.format("MMMM")} ${day}${suffix()} at ${date.format(
        "hh:mm a z"
      )}`;
    }
  }, [lastOfferExpiredAt]);

  const loanTypeText = useMemo(() => {
    if (!data?.loanType) return "";

    return (
      data?.loanConfig?.customProductAlias ||
      LOAN_PRODUCT_DISPLAY_NAME[data.loanType as LoanProductType] ||
      "Loan"
    );
  }, [data?.loanType]);
  const repayableByDPFlag = data?.loanConfig?.repayableByDP;

  const showAnnualInterestOnOfferFlag =
    !!data?.loanConfig?.showAnnualInterestOnOffer;

  const loanType = data?.loanType;

  if (loanOfferError) {
    return <LoanOfferError />;
  }

  if (isLoading) {
    return (
      <div className="cx-flex cx-flex-col cx-items-center cx-max-w-[960px] cx-w-full">
        <div className="cx-animate-spin cx-w-8 cx-h-8 cx-text-brand-primary-regular">
          <ButtonLoaderIcon />
        </div>
      </div>
    );
  }

  // This function determines whether to show interest rate annually or monthly
  const getFormattedInterestRate = (interestRate: number) => {
    const interestFrequency = data?.interestFrequency;

    if (
      showAnnualInterestOnOfferFlag &&
      interestFrequency === InterestFrequency.MONTHLY
    ) {
      return (interestRate * 12).toFixed(2);
    } else if (
      !showAnnualInterestOnOfferFlag &&
      interestFrequency === InterestFrequency.ANNUALLY
    ) {
      return (interestRate / 12).toFixed(2);
    } else {
      return interestRate.toFixed(2);
    }
  };

  const getFormattedInterestRateForCrediblexPlatform = (
    interestRate: number
  ) => {
    if (
      showAnnualInterestOnOfferFlag &&
      // For LOC, we always show monthly interest rate in the UI
      !isLOC
    ) {
      return interestRate.toFixed(2);
    } else {
      return (interestRate / 12).toFixed(2);
    }
  };

  const generatePaymentSchedule = (): string[] => {
    const initialDate = DateTime.now();
    const totalInstallments = convertBnplMonthsToInstallments(
      data.loanDuration
    );
    return Array.from({ length: totalInstallments }, (_, index) => {
      const date =
        index === 0
          ? initialDate
          : getFutureDate(1, "months", initialDate.plus({ months: index - 1 }));
      return date.toFormat("dd MMM yyyy");
    });
  };

  if (loanOfferLoading || themeLoading) {
    return (
      <div className="cx-flex cx-flex-col cx-items-center cx-max-w-[960px] cx-w-full">
        <div className="cx-animate-spin cx-w-8 cx-h-8 cx-text-brand-primary-regular">
          <ButtonLoaderIcon />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="cx-grow cx-w-full cx-max-w-[960px] cx-flex cx-flex-col cx-items-center cx-py-12">
        <div className="cx-mb-6">
          <ReadyClockIcon />
        </div>
        <div className="cx-text-text-primary cx-font-bold cx-text-3xl cx-text-center cx-mb-2">
          {isLOC
            ? "Your Line of credit offer is ready to go"
            : uiLocalizationData?.loanOfferMicroflow?.loanOfferStartPage
                ?.pageHeading ?? "Your loan offer is ready"}
        </div>

        <Tooltip
          content={
            <div className="cx-flex cx-flex-row cx-items-center">
              <InfoIcon
                height={16}
                width={16}
                className="cx-stroke-brand-white cx-mr-2"
              />
              <div className="cx-text-sm">
                Loan offer has been updated on {formattedDate}
              </div>
            </div>
          }
          open={isAlternateOffer}
          position={"bottom center"}
          closeOnDocumentClick={false}
          isEnabled={isAlternateOffer}
          trigger={
            <div className="cx-text-center cx-text-text-secondary cx-mb-6">
              {!isLOC && (
                <span className="cx-text-text-secondary">
                  {uiLocalizationData?.loanOfferMicroflow?.loanOfferStartPage
                    ?.pageDescription ??
                    "Please review the loan details, this offer is valid until"}{" "}
                  {DateTime.fromFormat(data?.validity, "yyyy-MM-dd").toFormat(
                    "dd MMM yyyy"
                  )}
                  .
                </span>
              )}
              {isLOC && (
                <span className="cx-text-text-secondary cx-text-gray-400">
                  {"This offer is valid until"}{" "}
                  {format(new Date(data?.validity), "do 'of' MMMM yyyy")}.
                </span>
              )}
            </div>
          }
        />
        {isAlternateOffer && (
          <div className="cx-text-text-secondary cx-text-center cx-mb-12"></div>
        )}

        {data?.losPlatform === LOSPlatform.BIZ2X && (
          <div className="cx-mb-10">
            <Button
              outlined
              slim
              label={
                <div className="cx-flex cx-flex-row cx-items-center cx-gap-2">
                  <div className="cx-w-4 cx-h-4">
                    <DocumentIcon className="cx-stroke-2" />
                  </div>

                  <div className="cx-text-interaction-text-default cx-text-sm cx-font-semibold">
                    Download as PDF
                  </div>
                </div>
              }
              onClick={() => {
                window.open(data?.link, "_blank");
              }}
            />
          </div>
        )}

        <div className="cx-w-full cx-flex cx-flex-wrap cx-bg-background-default cx-rounded-lg cx-shadow-base cx-gap-x-10 cx-px-10 cx-py-6 cx-mb-10 cx-pb-10">
          {(() => {
            const loanInfoItems: LoanInfoItemProps[] = [];

            loanInfoItems.push({
              label: "Business Name",
              text: data?.companyName,
            });

            if (isLOC) {
              loanInfoItems.push({
                label: "Product name",
                text: data?.loanConfig?.customProductAlias || loanTypeText,
              });
            }
            loanInfoItems.push({
              label:
                uiLocalizationData?.loanOfferMicroflow?.loanOfferStartPage
                  ?.approvedAmountLabel ||
                `Approved ${isLOC ? "Line of credit limit" : "loan amount"}`,
              subText: renderAED(data?.approvedAmount),
            });
            if (
              !uiLocalizationData?.loanOfferMicroflow?.loanOfferStartPage
                ?.hideLoanType &&
              !isLOC
            ) {
              loanInfoItems.push({ label: "Loan type", text: loanTypeText });
            }
            if (
              !uiLocalizationData?.loanOfferMicroflow?.loanOfferStartPage
                ?.hideMonthlyPayment &&
              (data?.loanType === LoanProductType.FIXED_TERM ||
                data?.loanType === LoanProductType.BNPL)
            ) {
              loanInfoItems.push({
                label: "Monthly payment",
                text: renderAED(data?.installmentAmount),
              });
            }
            // loanInfoItems.push({ label: getCopyText("INTEREST_RATE", "loanType", loanType)!(loanType),
            //                      text: `Fixed, ${data?.loanType !== LoanProductType.BNPL ? `${data?.annualInterestRate}%` : `${calculateMonthlyInterestRate(data?.annualInterestRate)}%`}`});
            if (
              !loanConfig?.hideInterestPaymentOnOffer &&
              data?.loanType !== LoanProductType.POS_REVENUE_BASED_FINANCING &&
              !isLOC
            ) {
              loanInfoItems.push({
                label:
                  uiLocalizationData?.loanOfferMicroflow?.loanOfferStartPage
                    ?.totalInterestAmountLabel ||
                  getCopyText(
                    "TOTAL_INTEREST_PAID",
                    "repayableByDP",
                    repayableByDPFlag
                  )!(),
                text: renderAED(Number(data?.totalInterestPaid)),
              });
            }
            if (
              data?.loanType !== LoanProductType.POS_REVENUE_BASED_FINANCING &&
              !isLOC
            ) {
              loanInfoItems.push({
                label: isLOC
                  ? getCopyText("INTEREST_RATE", "loanType", loanType)!(
                      loanType
                    )
                  : getCopyText(
                      "INTEREST_RATE",
                      "showAnnualInterestOnOffer",
                      showAnnualInterestOnOfferFlag
                    )!(),
                text: `Fixed, ${
                  data?.losPlatform === LOSPlatform.CREDIBLEX
                    ? getFormattedInterestRateForCrediblexPlatform(
                        data?.interestRate
                      )
                    : getFormattedInterestRate(data?.interestRate)
                }%`,
              });
            }
            if (repayableByDPFlag) {
              loanInfoItems.push({
                label: "Invoice Due Date",
                text: invoiceDueDate,
              });
            }
            if (
              !loanConfig?.hideInterestPaymentOnOffer &&
              !isLOC &&
              data.arrangementFee !== 0
            ) {
              loanInfoItems.push({
                label:
                  loanType === LoanProductType.PAYABLE_FINANCING
                    ? getCopyText("TOTAL_REPAYMENT", "loanType", loanType)!()
                    : getCopyText(
                        "TOTAL_REPAYMENT",
                        "repayableByDP",
                        repayableByDPFlag
                      )!(),
                text: renderAED(
                  Number(data?.approvedAmount) - Number(data?.arrangementFee)
                ),
              });
            }
            if (loanType == LoanProductType.REVENUE_BASED_FINANCING) {
              loanInfoItems.push({
                label: "Total repayment amount",
                text: renderAED(
                  Number(data?.totalInterestPaid ?? 0) +
                    Number(data?.approvedAmount ?? 0)
                ),
              });
            }
            if (data.loanType == LoanProductType.POS_REVENUE_BASED_FINANCING) {
              loanInfoItems.push({
                label: "Indicative total fees",
                text: renderAED(data?.arrangementFee),
              });
            }
            if (data.loanType == LoanProductType.INVOICE_DISCOUNTING) {
              loanInfoItems.push({
                label: "Loan Duration",
                text:
                  data?.loanDuration === 1
                    ? "1 Day"
                    : `${data.loanDuration} Days`,
              });
            }
            if (
              [
                LoanProductType.FIXED_TERM,
                LoanProductType.REVENUE_BASED_FINANCING,
                LoanProductType.LOC_INVOICE_DISCOUNTING,
                LoanProductType.LOC_PAYABLE_FINANCING,
              ].includes(data.loanType)
            ) {
              loanInfoItems.push({
                label: isLOC ? "Duration" : "Loan Duration",
                text:
                  data?.loanDuration === 1
                    ? "1 Month"
                    : `${data.loanDuration} Months`,
              });
            }
            if (data.loanType == LoanProductType.POS_REVENUE_BASED_FINANCING) {
              loanInfoItems.push({
                label: "Loan Duration",
                text:
                  data?.loanDuration === 1
                    ? "1 Day"
                    : `${data.loanDuration} Days`,
              });
            }
            if (loanConfig?.disbursementAccountProvided) {
              if (data.bankName?.length > 0) {
                loanInfoItems.push({ label: "Bank Name", text: data.bankName });
              }
              if (data.IBAN?.length > 0) {
                loanInfoItems.push({ label: "IBAN", text: data.IBAN });
              }
            }
            loanInfoItems.push({
              label: "Cash Margin",
              text: data?.cashMargin
                ? renderAED((data?.cashMargin / 100) * data?.approvedAmount)
                : "—",
            });
            if (data.loanType !== LoanProductType.POS_REVENUE_BASED_FINANCING) {
              if (data?.losPlatform === LOSPlatform.CREDIBLEX) {
                loanInfoItems.push({
                  label: "Processing Fee",
                  text: data?.processingFee
                    ? renderAED(data?.processingFee)
                    : "—",
                });
              } else {
                loanInfoItems.push({
                  label: "Processing fee",
                  text: data?.loanConfig?.processingFee
                    ? renderAED(data?.loanConfig?.processingFee)
                    : "—",
                });
              }
            }
            // TODO: This is to be enabled once Disbursement date logic is fixed from backend.
            // loanInfoItems.push({
            //   label: "Tentative Disbursement Date",
            //   text: data?.disbursementDate
            //     ? `${DateTime.fromISO(data?.disbursementDate).toFormat(
            //         "dd MMM, yyyy"
            //       )}`
            //     : "—",
            // });
            return loanInfoItems.map((item, index) => (
              <LoanInfoItem
                key={index}
                {...item}
                dividerClass={
                  index === loanInfoItems.length - 1
                    ? "cx-hidden"
                    : index === loanInfoItems.length - 2
                    ? "md:cx-hidden"
                    : ""
                }
              />
            ));
          })()}
        </div>

        {loanType === LoanProductType.BNPL && (
          <div
            className="
              bnpl-transactions cx-w-full cx-flex cx-flex-wrap cx-bg-background-default 
              cx-rounded-lg cx-shadow-base cx-gap-x-10 cx-px-10 cx-py-14 cx-mb-10"
          >
            <div className="cx-container cx-flex cx-gap-2 cx-items-center cx-pb-8 cx-relative">
              <p className="cx-font-bold cx-text-black cx-text-2xl">
                Payment schedule
              </p>
            </div>
            {generatePaymentSchedule().map((date, index) => (
              <PaymentScheduleRow
                key={index}
                installmentNo={index}
                isLast={index === data?.loanDuration + 1}
                isFirst={index === 0}
                monthlyPayable={data.installmentAmount}
                currencyUnit={data?.loanConfig?.amount?.unit}
                date={date}
                isReviewScreen={true}
              />
            ))}
          </div>
        )}

        {isLOC && (
          <div className="cx-w-full cx-bg-background-default cx-rounded-lg cx-shadow-base cx-px-8 cx-py-6 cx-mb-10">
            <div className="cx-text-text-primary cx-font-bold cx-text-lg cx-mb-4">
              Invoice terms
            </div>
            <div className="cx-flex cx-flex-wrap cx-gap-x-10">
              {(() => {
                const invoiceTermsItems: LoanInfoItemProps[] = [];

                invoiceTermsItems.push({
                  label: "Stale days",
                  text:
                    data?.staleDays != null ? `${data.staleDays} days` : "—",
                });
                invoiceTermsItems.push({
                  label: "Maximum invoice tenure",
                  text:
                    data?.maxInvoiceTenure != null
                      ? `${data.maxInvoiceTenure} days`
                      : "—",
                });

                return invoiceTermsItems.map((item, index) => (
                  <LoanInfoItem
                    key={index}
                    {...item}
                    dividerClass={
                      index === invoiceTermsItems.length - 1
                        ? "cx-hidden"
                        : index === invoiceTermsItems.length - 2
                        ? "md:cx-hidden"
                        : ""
                    }
                  />
                ));
              })()}
            </div>
          </div>
        )}

        {isLOC && (
          <div className="cx-w-full cx-bg-background-default cx-rounded-lg cx-shadow-base cx-px-8 cx-py-6 cx-mb-10">
            <div className="cx-text-text-primary cx-font-bold cx-text-lg cx-mb-4">
              Active{" "}
              {getCopyText("VENDOR_TYPE_TEXT", "vendorType", true)!(
                data.loanType === LoanProductType.LOC_INVOICE_DISCOUNTING
                  ? LoanVendorType.BUYER
                  : LoanVendorType.SUPPLIER
              )}
            </div>
            <div className="cx-grid cx-grid-cols-1 md:cx-grid-cols-2 cx-gap-6">
              {vendors?.map((vendor: any) => (
                <div
                  key={vendor.loanVendorId}
                  className="cx-flex cx-items-center cx-p-4 cx-bg-white cx-rounded-lg cx-shadow-sm cx-border cx-border-gray-200"
                >
                  <div className="cx-text-2xl cx-mr-4  cx-mt-1">
                    {
                      countries.find(
                        (country) => country.cca3 === vendor.country
                      )?.flag
                    }
                  </div>
                  <div className="cx-flex-1 cx-text-text-primary cx-font-medium">
                    {vendor.name}
                  </div>
                  <div className="cx-w-5 cx-h-5 cx-text-green-500">
                    <SuccessIcon />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="cx-w-full cx-flex cx-flex-row cx-justify-center cx-gap-x-6">
          <div className="cx-w-full cx-max-w-[400px]">
            <StepNextButton
              label="Accept"
              onClick={handleApprove}
              disabled={data?.status === "REVOKED"}
            />
          </div>
          <div className="cx-w-full cx-max-w-[400px]">
            <Button
              label="Decline"
              type="button"
              fullWidth
              outlined
              onClick={handleDecline}
            />
          </div>
        </div>
      </div>
      <LoanOfferRejectModel
        isOpen={showRejectModal}
        isLOC={isLOC}
        comment={cancelComment}
        onChangeComment={setCancelComment}
        isLoading={declineLoading}
        onConfirm={handleDeclineConfirm}
        onCancel={() => {
          setShowRejectModal(false);
          setCancelComment(""); // reset comment on cancel / go back
        }}
      />
    </>
  );
};

export default LoanOfferStart;
